import React, { useState } from 'react';
import { Show } from '@refinedev/antd';
import { useShow } from '@refinedev/core';
import { Tag, theme, Button, Modal, Typography, Space } from 'antd';
import { ApiOutlined } from '@ant-design/icons';
import { LlmProviderDto, LlmProviderType, TestProviderConnectionResultDto } from '@wellverse/shared-ts';
import { axiosInstance, API_BASE_URL } from '../../../providers/dataProvider';
import { Field } from '../../../components/Field';

const typeColors: Record<LlmProviderType, string> = {
  [LlmProviderType.OPENAI]: '#52c41a',
  [LlmProviderType.ANTHROPIC]: '#722ed1',
  [LlmProviderType.CUSTOM]: '#1890ff',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});


export const LlmProviderShow: React.FC = () => {
  const { token } = theme.useToken();
  const { queryResult } = useShow<LlmProviderDto>({ resource: 'llm-providers' });
  const item = queryResult?.data?.data;

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestProviderConnectionResultDto | null>(null);
  const [testModalOpen, setTestModalOpen] = useState(false);

  if (!item) {
    return <div style={{ color: token.colorTextSecondary }}>Loading...</div>;
  }

  const record = item as LlmProviderDto & {
    createdAt?: string;
    updatedAt?: string;
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/admin/llm/providers/${record.id}/test`,
      );
      setTestResult(response.data);
      setTestModalOpen(true);
    } catch (error: any) {
      setTestResult({
        success: false,
        model: record.defaultModel,
        responsePreview: '',
        latencyMs: 0,
        error: error?.response?.data?.message || error?.message || 'Unknown error',
      });
      setTestModalOpen(true);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Show resource="llm-providers">
      <div style={{ padding: '8px 0' }}>
        <Field label="Name">{record.name}</Field>
        <Field label="Type">
          <Tag style={tagStyle(typeColors[record.type] || '#898989')}>
            {record.type}
          </Tag>
        </Field>
        <Field label="Has API Key">
          <Tag style={tagStyle(record.hasApiKey ? '#3ecf8e' : '#ff4d4f')}>
            {record.hasApiKey ? 'Yes' : 'No'}
          </Tag>
        </Field>
        {record.maskedApiKey && (
          <Field label="API Key">
            <Typography.Text code>{record.maskedApiKey}</Typography.Text>
          </Field>
        )}
        <Field label="Base URL">{record.baseUrl || '—'}</Field>
        <Field label="Default Model">{record.defaultModel}</Field>
        <Field label="Model List">
          {record.modelList && record.modelList.length > 0 ? (
            <Space wrap size={4}>
              {record.modelList.map((m) => (
                <Tag key={m} style={{ borderRadius: 9999, padding: '2px 8px' }}>{m}</Tag>
              ))}
            </Space>
          ) : '—'}
        </Field>
        <Field label="Max Tokens">{record.maxTokens}</Field>
        <Field label="Priority">{record.priority}</Field>
        <Field label="Active">
          <Tag style={tagStyle(record.isActive ? '#3ecf8e' : '#898989')}>
            {record.isActive ? 'Active' : 'Inactive'}
          </Tag>
        </Field>
        {record.createdAt && (
          <Field label="Created At">{new Date(record.createdAt).toLocaleString()}</Field>
        )}
        {record.updatedAt && (
          <Field label="Updated At">{new Date(record.updatedAt).toLocaleString()}</Field>
        )}

        <div style={{ marginTop: 24 }}>
          <Button
            type="primary"
            icon={<ApiOutlined />}
            loading={testing}
            onClick={handleTestConnection}
          >
            Test Connection
          </Button>
        </div>
      </div>

      <Modal
        title="Connection Test Result"
        open={testModalOpen}
        onCancel={() => setTestModalOpen(false)}
        footer={null}
        width={600}
      >
        {testResult && (
          <div>
            <Field label="Status">
              <Tag style={tagStyle(testResult.success ? '#3ecf8e' : '#ff4d4f')}>
                {testResult.success ? 'Success' : 'Failed'}
              </Tag>
            </Field>
            <Field label="Model">{testResult.model}</Field>
            <Field label="Latency">{testResult.latencyMs}ms</Field>
            {testResult.inputTokens !== undefined && (
              <Field label="Input Tokens">{testResult.inputTokens}</Field>
            )}
            {testResult.outputTokens !== undefined && (
              <Field label="Output Tokens">{testResult.outputTokens}</Field>
            )}
            {testResult.responsePreview && (
              <Field label="Response Preview">
                <Typography.Paragraph
                  style={{ background: token.colorBgLayout, padding: 12, borderRadius: 8, marginTop: 4 }}
                >
                  <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>
                    {testResult.responsePreview}
                  </pre>
                </Typography.Paragraph>
              </Field>
            )}
            {testResult.error && (
              <Field label="Error">
                <Typography.Text type="danger">{testResult.error}</Typography.Text>
              </Field>
            )}
          </div>
        )}
      </Modal>
    </Show>
  );
};
